import React from 'react';
import styled from 'styled-components';
import { FaGithub, FaInstagram, FaWhatsapp, FaFacebook } from 'react-icons/fa';
import { FiArrowUpRight } from 'react-icons/fi';
import scrollToSection from '../../utils/scrollToSection';

const FooterSection = styled.footer`
  margin-top: 60px;
  padding: 0 24px 80px;
`;

const WhiteBlock = styled.div`
  position: relative;
  background: #FFFFFF;
  color: ${({ theme }) => theme.colors.ink};
  border: 1px solid rgba(199, 199, 199, 0.5);
  border-radius: 40px;
  padding: 64px 48px 40px;
  overflow: hidden;
  box-shadow: rgba(199, 199, 199, 0.2) 0px 1px 3px -1px,
    rgba(199, 199, 199, 0.15) 0px 8px 20px -3px;

  @media (max-width: 768px) {
    padding: 48px 28px 32px;
    border-radius: 32px;
  }
`;

const Inner = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 48px 32px;
  align-items: flex-start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const Title = styled.h3`
  font-family: ${({ theme }) => theme.fonts.soft};
  font-size: clamp(2.2rem, 4vw, 3rem);
  font-weight: 400;
  line-height: 1.05;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.ink};
  margin-bottom: 14px;
`;

const Subtitle = styled.p`
  font-family: ${({ theme }) => theme.fonts.soft};
  font-size: 1.08rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.mutedDark || '#6B7280'};
  margin-bottom: 28px;
  max-width: 26ch;
`;

const ContactLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: ${({ theme }) => theme.colors.ink};
  color: #FFFFFF;
  font-family: ${({ theme }) => theme.fonts.soft};
  font-size: 1.02rem;
  font-weight: 400;
  padding: 14px 22px 14px 24px;
  border-radius: 999px;
  transition: transform 0.25s ease, background 0.25s ease;

  svg {
    transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  }

  &:hover {
    background: ${({ theme }) => theme.colors.accent};
  }

  &:hover svg {
    transform: translate(3px, -3px);
  }
`;

const LinkGroup = styled.div``;

const ColTitle = styled.div`
  font-family: ${({ theme }) => theme.fonts.soft};
  font-size: 1.08rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.mutedDark || '#6B7280'};
  margin-bottom: 18px;
`;

const Links = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const FooterLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: ${({ theme }) => theme.fonts.soft};
  font-size: 1.08rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.ink};
  transition: color 0.2s ease;
  width: fit-content;

  svg {
    color: ${({ theme }) => theme.colors.mutedDark || '#6B7280'};
    transition: color 0.2s ease;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }

  &:hover svg {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const FooterNavLink = styled.a`
  font-family: ${({ theme }) => theme.fonts.soft};
  font-size: 1.08rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.ink};
  cursor: pointer;
  transition: color 0.2s ease;
  width: fit-content;

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const Bottom = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding-top: 24px;
  margin-top: 32px;
`;

const Copy = styled.div`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.mutedDark || '#6B7280'};
`;

const Socials = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const IconLink = styled.a`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.15);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.ink};
  transition: background 0.25s ease, color 0.25s ease, border-color 0.25s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.ink};
    color: #FFFFFF;
    border-color: ${({ theme }) => theme.colors.ink};
  }
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterSection>
      <WhiteBlock>
        <Inner>
          <div>
            <Title>Let&apos;s connect</Title>
            <Subtitle>Open to new projects, collaborations, and conversations.</Subtitle>
            <ContactLink href="mailto:jabezllave@gmail.com">
              Contact me
              <FiArrowUpRight size={18} />
            </ContactLink>
          </div>

          <LinkGroup>
            <ColTitle>Navigation</ColTitle>
            <Links>
              <FooterNavLink href="#home" onClick={(e) => { e.preventDefault(); scrollToSection('home'); }}>
                Home
              </FooterNavLink>
              <FooterNavLink href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }}>
                About
              </FooterNavLink>
              <FooterNavLink href="#projects" onClick={(e) => { e.preventDefault(); scrollToSection('projects'); }}>
                Case Studies
              </FooterNavLink>
              <FooterNavLink href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>
                Contact
              </FooterNavLink>
            </Links>
          </LinkGroup>

          <LinkGroup>
            <ColTitle>Elsewhere</ColTitle>
            <Links>
              <FooterLink href="https://github.com/Jabex113" target="_blank" rel="noopener noreferrer">
                <FaGithub size={16} />
                GitHub
              </FooterLink>
              <FooterLink href="https://www.facebook.com/jabez.llave" target="_blank" rel="noopener noreferrer">
                <FaFacebook size={16} />
                Facebook
              </FooterLink>
              <FooterLink href="https://instagram.com/lljbz69_" target="_blank" rel="noopener noreferrer">
                <FaInstagram size={16} />
                Instagram
              </FooterLink>
              <FooterLink href="https://wa.me/639636424793" target="_blank" rel="noopener noreferrer">
                <FaWhatsapp size={16} />
                WhatsApp
              </FooterLink>
            </Links>
          </LinkGroup>
        </Inner>

        <Bottom>
          <Copy>© {currentYear} – Jabez Llave</Copy>
          <Socials>
            <IconLink href="mailto:jabezllave@gmail.com" aria-label="Email">
              <span className="material-symbols-rounded" style={{ fontSize: 18 }}>mail</span>
            </IconLink>
            <IconLink href="https://github.com/Jabex113" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
              <FaGithub size={16} />
            </IconLink>
            <IconLink href="https://instagram.com/lljbz69_" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <FaInstagram size={16} />
            </IconLink>
            <IconLink href="https://wa.me/639636424793" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
              <FaWhatsapp size={16} />
            </IconLink>
          </Socials>
        </Bottom>
      </WhiteBlock>
    </FooterSection>
  );
};

export default Footer;
